import { DateTime } from 'luxon';

import ReportProgress from '@/common/models/ReportProgress';
import { ITargetingCriteriaAPI } from '@/common/models/targeting-criteria/TargetingCriteria';
import { DASH } from '@/globals';

import AvailsResult from './AvailsResult';

export interface IAvailsProgressParametersAPI {
  targeting_criteria: ITargetingCriteriaAPI;
  organization_id: number;
  start_date: string;
  end_date: string;
  ad_duration: number;
  utc_now: string;
  get_by_unit: boolean;
  optimize_impressions: boolean;
}

export default class AvailsProgress extends ReportProgress {
  id = -1;

  organizationId: number;

  createdOnUtc: string;

  parameters: IAvailsProgressParametersAPI;

  statusMessage: never; // This is fed with parsed JSON.

  result: AvailsResult;

  constructor(
    id = -1,
    organizationId: number,
    createdOnUtc: string,
    executionStartedTimeUtc: string,
    executionFinishedTimeUtc: string,
    parameters: IAvailsProgressParametersAPI,
    statusCode: string,
    statusMessage: never,
    progress: number,
    result: AvailsResult,
  ) {
    super();
    this.id = id;
    this.organizationId = organizationId;
    this.createdOnUtc = createdOnUtc;
    this.executionStartedTimeUtc = executionStartedTimeUtc;
    this.executionFinishedTimeUtc = executionFinishedTimeUtc;
    this.parameters = parameters;
    this.statusCode = statusCode;
    this.statusMessage = statusMessage;
    this.progress = progress;
    this.result = result;
  }

  get createdOnUtcDateTime(): DateTime {
    if (this.createdOnUtc !== null) {
      return DateTime.fromISO(this.createdOnUtc, { zone: 'utc' });
    }
    return null;
  }

  get createdOnFormatted(): string {
    if (this.executionStartedTimeUtc !== null) {
      return DateTime.fromISO(this.createdOnUtc, { zone: 'utc' })
        .toLocal()
        .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
    }
    return DASH;
  }

  static fromApi(apiObject) {
    let statusMessage = '';
    try {
      statusMessage = apiObject.status_message !== null ? JSON.parse(apiObject.status_message) : null;
    } catch (e) {
      statusMessage = apiObject.status_message;
    }
    return new AvailsProgress(
      apiObject.checkavailsexecution_id,
      apiObject.organization_id,
      apiObject.created_on_utc,
      apiObject.execution_started_time_utc,
      apiObject.execution_finished_time_utc,
      apiObject.parameters,
      apiObject.status_code,
      statusMessage as never,
      apiObject.progress,
      apiObject.result ? AvailsResult.fromApi(apiObject.result) : null,
    );
  }

  static fromObject(obj) {
    return new AvailsProgress(
      obj.id,
      obj.organizationId,
      obj.createdOnUtc,
      obj.executionStartedTimeUtc,
      obj.executionFinishedTimeUtc,
      obj.parameters,
      obj.statusCode,
      obj.statusMessage as never,
      obj.progress,
      obj.result,
    );
  }

  toApi() {
    return {
      checkavailsexecution_id: this.id >= 0 ? this.id : undefined,
      created_on_utc: this.createdOnUtc,
      execution_started_time_utc: this.executionStartedTimeUtc,
      execution_finished_time_utc: this.executionFinishedTimeUtc,
      organization_id: this.organizationId,
      parameters: this.parameters,
      status_code: this.statusCode,
      status_message: this.statusMessage,
      progress: this.progress,
      result: this.result,
    };
  }
}
